import { createAction, handleActions, Reducer } from 'redux-actions';
import { TraceStatus } from '@redux/types';

export interface PartnerState {
  isLoading: boolean;
  partnerList: any[];
  searchTerm: string;
  trace: { status: TraceStatus; message: string };
}

export const initialState: PartnerState = {
  isLoading: false,
  partnerList: [],
  searchTerm: '',
  trace: {
    status: TraceStatus.Info,
    message: '',
  },
};

type PartnerReducer<T = PartnerState> = Reducer<PartnerState, T>;

// Actions

export const PARTNER_LIST_WITH_ACCESS_DETAILS_FETCH_STARTED = 'partner/PARTNER_LIST_WITH_ACCESS_DETAILS_FETCH_STARTED';
export const PARTNER_LIST_WITH_ACCESS_DETAILS_FETCH_SUCCEED = 'partner/PARTNER_LIST_WITH_ACCESS_DETAILS_FETCH_SUCCEED';
export const PARTNER_LIST_WITH_ACCESS_DETAILS_FETCH_FAILED = 'partner/PARTNER_LIST_WITH_ACCESS_DETAILS_FETCH_FAILED';
export const PARTNER_TOGGLE = 'partner/PARTNER_TOGGLE';
export const PARTNER_TOGGLE_ALL = 'partner/PARTNER_TOGGLE_ALL';
export const PARTNER_SEARCH = 'partner/PARTNER_SEARCH';
export const RESET_STATE = 'partner/RESET_STATE';

// Action creators

export const fetchPartnerListWithAccessDetails = createAction(PARTNER_LIST_WITH_ACCESS_DETAILS_FETCH_STARTED);
export const fetchPartnerListWithAccessDetailsSucceed = createAction(PARTNER_LIST_WITH_ACCESS_DETAILS_FETCH_SUCCEED);
export const fetchPartnerListWithAccessDetailsFailed = createAction(PARTNER_LIST_WITH_ACCESS_DETAILS_FETCH_FAILED);
export const togglePartner = createAction(PARTNER_TOGGLE);
export const toggleAllPartners = createAction(PARTNER_TOGGLE_ALL);
export const searchPartnerList = createAction(PARTNER_SEARCH);
export const resetState = createAction(RESET_STATE);

// Reducers

const handleFetchPartnerListWithAccessDetails: PartnerReducer = state => ({
  ...state,
  isLoading: true,
});

const handleFetchPartnerListWithAccessDetailsSucceed: PartnerReducer<any[]> = (state, { payload }) => ({
  ...state,
  isLoading: false,
  partnerList: payload || [],
});

const handleFetchPartnerListWithAccessDetailsFailed: PartnerReducer<any[]> = state => ({
  ...state,
  trace: {
    status: TraceStatus.Error,
    message: t('error.an-error-occurred.contact-support'),
  },
  isLoading: false,
});

const handleTogglePartner: PartnerReducer<any> = (state, { payload }) => {
  const partnerList = state.partnerList.map(partner => {
    if (partner.id === payload.id) {
      return { ...partner, selected: !partner.selected };
    }

    return partner;
  });

  return {
    ...state,
    partnerList,
  };
};

const handleToggleAllPartners: PartnerReducer = state => {
  const toggleState = state.partnerList.length > 0 && state.partnerList.every(partner => partner.selected);

  return {
    ...state,
    partnerList: state.partnerList.map(partner => ({ ...partner, selected: !toggleState })),
  };
};

const handleSearchPartner: PartnerReducer<string> = (state, { payload }) => ({
  ...state,
  searchTerm: payload || '',
});

const handleResetState = () => ({
  ...initialState,
});

export default handleActions<PartnerState, any>(
  {
    [PARTNER_LIST_WITH_ACCESS_DETAILS_FETCH_STARTED]: handleFetchPartnerListWithAccessDetails,
    [PARTNER_LIST_WITH_ACCESS_DETAILS_FETCH_SUCCEED]: handleFetchPartnerListWithAccessDetailsSucceed,
    [PARTNER_LIST_WITH_ACCESS_DETAILS_FETCH_FAILED]: handleFetchPartnerListWithAccessDetailsFailed,
    [PARTNER_TOGGLE]: handleTogglePartner,
    [PARTNER_TOGGLE_ALL]: handleToggleAllPartners,
    [PARTNER_SEARCH]: handleSearchPartner,
    [RESET_STATE]: handleResetState,
  },
  initialState,
);

// Selectors

export const getPartnerState = (state): PartnerState => state.partner;
